"use client";

import { useMemo, useState } from "react";
import { Smartphone, Monitor } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import type { AnalyzeData } from "@/types/analyze";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { PerformanceDetails } from "@/components/PageSpeed/PerformanceDetails";
import { AccessibilityDetails } from "@/components/PageSpeed/AccessibilityDetails";
import { BestPracticesDetails } from "@/components/PageSpeed/BestPracticesDetails";
import { SeoDetails } from "@/components/PageSpeed/SeoDetails";

interface ScoreSet {
  performance: number;
  accessibility: number;
  bestPractices: number;
  seo: number;
}

// Minimal shapes for the summary object we actually read
interface SummaryCheckItem {
  title?: string;
  normalized: { achieved: number; max: number };
}

interface SummaryCategoryChecks {
  checks?: SummaryCheckItem[];
}

interface SummaryPerformanceMetric {
  value?: number;
  thresholds?: { t1?: number; t2?: number };
}

interface SummaryPerformance {
  metrics?: Record<string, SummaryPerformanceMetric>;
}

interface PageSummaryShape {
  categories?: {
    performance?: SummaryPerformance;
    seo?: SummaryCategoryChecks;
    bestPractices?: SummaryCategoryChecks;
    accessibility?: SummaryCategoryChecks;
  };
}

interface PageSpeedOverviewProps {
  pageSpeed?: AnalyzeData["pageSpeed"];
  initialDevice?: "mobile" | "desktop";
  className?: string;
  defaultExpanded?: boolean;
  planType?: AnalyzeData["planType"];
}

export function PageSpeedOverview({
  pageSpeed,
  initialDevice = "mobile",
  className = "",
  defaultExpanded = true,
  planType,
}: PageSpeedOverviewProps) {
  const [device, setDevice] = useState<"mobile" | "desktop">(initialDevice);

  const extractScoresFromDeviceData = (
    deviceData: AnalyzeData["pageSpeed"]["mobile"]
  ): ScoreSet | undefined => {
    if (!deviceData || !deviceData.scores) return undefined;

    const performanceScore =
      typeof deviceData.scores.performanceScore === "number"
        ? deviceData.scores.performanceScore
        : undefined;
    const accessibilityScore =
      typeof deviceData.scores.accessibilityScore === "number"
        ? deviceData.scores.accessibilityScore
        : undefined;
    const bestPracticesScore =
      typeof deviceData.scores.bestPracticesScore === "number"
        ? deviceData.scores.bestPracticesScore
        : undefined;
    const seoScore =
      typeof deviceData.scores.seoScore === "number"
        ? deviceData.scores.seoScore
        : undefined;

    if (
      performanceScore === undefined &&
      accessibilityScore === undefined &&
      bestPracticesScore === undefined &&
      seoScore === undefined
    ) {
      return undefined;
    }

    return {
      performance: performanceScore ?? 0,
      accessibility: accessibilityScore ?? 0,
      bestPractices: bestPracticesScore ?? 0,
      seo: seoScore ?? 0,
    };
  };

  const mobileScores: ScoreSet | undefined = useMemo(
    () => extractScoresFromDeviceData(pageSpeed?.mobile ?? null),
    [pageSpeed]
  );
  const desktopScores: ScoreSet | undefined = useMemo(
    () => extractScoresFromDeviceData(pageSpeed?.desktop ?? null),
    [pageSpeed]
  );

  const currentDeviceScores: ScoreSet | undefined =
    device === "mobile" ? mobileScores : desktopScores;

  // Combined overall score across mobile and desktop
  const overallScore: number | undefined = useMemo(() => {
    const allScoreSets: ScoreSet[] = [];
    if (mobileScores) allScoreSets.push(mobileScores);
    if (desktopScores) allScoreSets.push(desktopScores);
    if (allScoreSets.length === 0) return undefined;

    const allScoreValues: number[] = allScoreSets.flatMap((scoreSet) => [
      scoreSet.performance,
      scoreSet.accessibility,
      scoreSet.bestPractices,
      scoreSet.seo,
    ]);
    const averageScore =
      allScoreValues.reduce((sum, score) => sum + score, 0) /
      Math.max(allScoreValues.length, 1);
    return Math.round(averageScore);
  }, [mobileScores, desktopScores]);

  // Count individual audit items by status for each category (red/orange/green)
  const categoryCounts = useMemo(() => {
    const countChecks = (
      checks: Array<{
        title?: string;
        normalized: { achieved: number; max: number };
      }>
    ) => {
      let green = 0; // 90-100% success ratio
      let orange = 0; // 10-89% success ratio
      let red = 0; // 0-9% success ratio

      checks.forEach((check) => {
        // Skip checks with no max points OR checks that are 0/0 (not applicable)
        if (
          check.normalized.max <= 0 ||
          (check.normalized.achieved === 0 && check.normalized.max === 0)
        ) {
          return;
        }

        const successRatio = check.normalized.achieved / check.normalized.max;

        if (successRatio >= 0.9) green++;
        else if (successRatio >= 0.1) orange++;
        else red++;
      });

      return { green, orange, red };
    };

    const getCategoryChecks = (
      deviceData: AnalyzeData["pageSpeed"]["mobile"],
      category: "seo" | "accessibility" | "bestPractices"
    ): SummaryCheckItem[] => {
      if (!deviceData?.summary) return [];
      const summary = deviceData.summary as PageSummaryShape;
      return summary.categories?.[category]?.checks ?? [];
    };

    // Get checks from both mobile and desktop
    const mobileSeoChecks = getCategoryChecks(pageSpeed?.mobile ?? null, "seo");
    const desktopSeoChecks = getCategoryChecks(
      pageSpeed?.desktop ?? null,
      "seo"
    );
    const mobileAccessibilityChecks = getCategoryChecks(
      pageSpeed?.mobile ?? null,
      "accessibility"
    );
    const desktopAccessibilityChecks = getCategoryChecks(
      pageSpeed?.desktop ?? null,
      "accessibility"
    );
    const mobileBestPracticesChecks = getCategoryChecks(
      pageSpeed?.mobile ?? null,
      "bestPractices"
    );
    const desktopBestPracticesChecks = getCategoryChecks(
      pageSpeed?.desktop ?? null,
      "bestPractices"
    );

    //

    // Performance has 5 core metrics: LCP, FCP, TBT, CLS, SpeedIndex
    // We count them as individual items (5 per device = 10 total)
    const getPerformanceMetrics = (
      deviceData: AnalyzeData["pageSpeed"]["mobile"]
    ): Array<{
      title: string;
      normalized: { achieved: number; max: number };
    }> => {
      if (!deviceData?.summary) return [];
      const summary = deviceData.summary as PageSummaryShape;
      const metrics = summary.categories?.performance?.metrics;
      if (!metrics) return [];

      // Always return 5 metrics: lcp, fcp, tbt, cls, speedIndex
      const metricKeys = ["lcp", "fcp", "tbt", "cls", "speedIndex"];
      return metricKeys
        .map((key) => {
          const metric = metrics[key];
          if (!metric) return null;

          // Use thresholds to determine color: value <= t1 = green, t1 < value <= t2 = orange, value > t2 = red
          const value = metric.value ?? 0;
          const t1 = metric.thresholds?.t1 ?? 0;
          const t2 = metric.thresholds?.t2 ?? 0;

          let achieved = 0;
          const max = 100;

          if (value <= t1) {
            achieved = 100; // Green
          } else if (value <= t2) {
            achieved = 50; // Orange
          } else {
            achieved = 0; // Red
          }

          return {
            title: key,
            normalized: {
              achieved: achieved,
              max: max,
            },
          };
        })
        .filter(
          (
            item
          ): item is {
            title: string;
            normalized: { achieved: number; max: number };
          } => item !== null
        );
    };

    const mobilePerformanceMetrics = getPerformanceMetrics(
      pageSpeed?.mobile ?? null
    );
    const desktopPerformanceMetrics = getPerformanceMetrics(
      pageSpeed?.desktop ?? null
    );

    const result = {
      performance: countChecks([
        ...mobilePerformanceMetrics,
        ...desktopPerformanceMetrics,
      ]),
      seo: countChecks([...mobileSeoChecks, ...desktopSeoChecks]),
      accessibility: countChecks([
        ...mobileAccessibilityChecks,
        ...desktopAccessibilityChecks,
      ]),
      bestPractices: countChecks([
        ...mobileBestPracticesChecks,
        ...desktopBestPracticesChecks,
      ]),
    };

    //

    return result;
  }, [pageSpeed]);

  // No errors in new format - simplified error handling
  const deviceErrors = useMemo(() => {
    const errorMap: Record<"mobile" | "desktop", string | undefined> = {
      mobile: undefined,
      desktop: undefined,
    };
    return errorMap;
  }, []);

  const getScoreColor = (score: number) => {
    if (score >= 80) return "var(--status-green)";
    if (score >= 60) return "var(--status-orange)";
    return "var(--status-red)";
  };

  // Mini donut chart for category items
  const MiniScoreDonut = ({
    label,
    score,
  }: {
    label: string;
    score: number;
  }) => {
    const data = [
      { name: "Score", value: score, fill: getScoreColor(score) },
      { name: "Remaining", value: 100 - score, fill: "transparent" },
    ];

    return (
      <div className="text-center space-y-3">
        <div className="relative h-32 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={38}
                outerRadius={50}
                strokeWidth={0}
                cornerRadius={6}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
                animationDuration={1000}
                animationEasing="ease-out"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${label}-${index}`} fill={entry.fill} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="text-2xl font-bold tracking-tight text-foreground">
              {score}
            </div>
          </div>
        </div>
        <div className="space-y-1">
          <h3 className="text-sm font-bold tracking-tight text-foreground">
            {label}
          </h3>
        </div>
      </div>
    );
  };

  // Calculate total counts across all categories
  const totalCounts = useMemo(() => {
    const allCounts = Object.values(categoryCounts);
    return allCounts.reduce(
      (acc, counts) => ({
        green: acc.green + counts.green,
        orange: acc.orange + counts.orange,
        red: acc.red + counts.red,
      }),
      { green: 0, orange: 0, red: 0 }
    );
  }, [categoryCounts]);

  return (
    <>
      {/* Right-aligned hint above the first section */}

      <CollapsibleSection
        title="PageSpeed"
        subtitle={overallScore?.toString()}
        subtitleColor={getScoreColor(overallScore ?? 0)}
        rightContent={
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-status-green-bg text-status-green-foreground px-2 py-0.5 text-[11px] font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-status-green" />
              {totalCounts.green}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-status-orange-bg text-status-orange-foreground px-2 py-0.5 text-[11px] font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-status-orange" />
              {totalCounts.orange}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-status-red-bg text-status-red-foreground px-2 py-0.5 text-[11px] font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-status-red" />
              {totalCounts.red}
            </span>
          </div>
        }
        className={`${className}`}
        defaultExpanded={defaultExpanded}
      >
        <div className="grid grid-cols-1 gap-6 px-3 pb-4">
          {/* Device switch - responsive positioning */}
          <div>
            <div className="flex items-center justify-center pb-4">
              <ToggleGroup
                type="single"
                value={device}
                onValueChange={(v) => v && setDevice(v as "mobile" | "desktop")}
                className="bg-muted/30 p-1 rounded-lg"
              >
                <ToggleGroupItem
                  value="mobile"
                  aria-label="Mobile"
                  className="h-12 px-6 data-[state=on]:bg-background data-[state=on]:shadow-sm data-[state=on]:border data-[state=on]:border-border/50"
                >
                  <Smartphone className="h-5 w-5" />
                  <span className="ml-2 text-sm font-medium">Mobile</span>
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="desktop"
                  aria-label="Desktop"
                  className="h-12 px-6 data-[state=on]:bg-background data-[state=on]:shadow-sm data-[state=on]:border data-[state=on]:border-border/50"
                >
                  <Monitor className="h-5 w-5" />
                  <span className="ml-2 text-sm font-medium">Desktop</span>
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            {currentDeviceScores ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 max-w-4xl mx-auto">
                <MiniScoreDonut
                  label="Performance"
                  score={currentDeviceScores.performance}
                />
                <MiniScoreDonut label="SEO" score={currentDeviceScores.seo} />
                <MiniScoreDonut
                  label="Best Practices"
                  score={currentDeviceScores.bestPractices}
                />
                <MiniScoreDonut
                  label="Accessibility"
                  score={currentDeviceScores.accessibility}
                />
              </div>
            ) : (
              <div className="h-28 w-full flex items-center justify-center text-xs text-muted-foreground border rounded-md">
                {device === "mobile"
                  ? deviceErrors.mobile || "No mobile PageSpeed data"
                  : deviceErrors.desktop || "No desktop PageSpeed data"}
              </div>
            )}
          </div>

          <div className="px-3 pb-3">
            <div className="space-y-4">
              <PerformanceDetails
                device={device}
                pageSpeed={pageSpeed}
                planType={planType}
              />
              <SeoDetails
                device={device}
                pageSpeed={pageSpeed}
                planType={planType}
              />
              <BestPracticesDetails
                device={device}
                pageSpeed={pageSpeed}
                planType={planType}
              />
              <AccessibilityDetails
                device={device}
                pageSpeed={pageSpeed}
                planType={planType}
              />
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </>
  );
}
